import { LinkButton, P } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { ControlCard } from 'sections/onsite-campaigns/pages/CampaignEditor/components/ControlCard';
import styled, { useTheme } from 'styled-components';
import { InfoTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/components/InfoTooltip';
import { SchedulingDefaultVariant } from './SchedulingDefaultVariant';
import { useSchedulingSelector } from '../../../../state/SplitTests/selectors/useSchedulingSelector';
import { useCallback, useState } from 'react';
import { ScheduledDatesModal } from './ScheduledDatesModal';

const SchedulingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing3};
`;

export interface SchedulingControlProps {
  splitTestId: string;
}

export const SchedulingControl = ({ splitTestId }: SchedulingControlProps) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const { startDate, startTime, endDate, endTime } = useSchedulingSelector({ splitTestId });

  const [isScheduledDatesModalOpen, setIsScheduledDatesModalOpen] = useState(false);
  const openScheduledDatesModal = useCallback(() => setIsScheduledDatesModalOpen(true), []);
  const closeScheduledDatesModal = useCallback(() => setIsScheduledDatesModalOpen(false), []);

  return (
    <>
      <ControlCard>
        <SchedulingContent>
          <P variant="body1">
            {t('onsite.editor.workflow.displayWebContentMessage.scheduling.description')}
            <span style={{ marginRight: theme.designToken.spacing1 }} />
            <InfoTooltip
              displayInline
              content={t('onsite.editor.workflow.displayWebContentMessage.scheduling.descriptionTooltip')}
            />
          </P>
          <div style={{ display: 'flex', gap: theme.designToken.spacing2, flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: theme.designToken.spacing1, flexDirection: 'column' }}>
              <P style={{ padding: 0, margin: 0 }} variant="body2">
                <strong>Starts:</strong> {startDate?.toLocaleDateString()}{' '}
                {startTime ? (
                  <>
                    <strong>at</strong> {startTime} UTC
                  </>
                ) : null}
              </P>

              <P style={{ padding: 0, margin: 0 }} variant="body2">
                <strong>Ends:</strong>{' '}
                {endDate ? (
                  <>
                    {endDate?.toLocaleDateString()}{' '}
                    {endTime ? (
                      <>
                        <strong>at</strong> {endTime} UTC
                      </>
                    ) : null}
                  </>
                ) : (
                  t('onsite.editor.workflow.displayWebContentMessage.scheduling.endDatePlaceholder')
                )}
              </P>
            </div>
            <LinkButton
              leftSideIcon="pen"
              onClick={openScheduledDatesModal}
              ariaLabel={t('onsite.editor.workflow.displayWebContentMessage.scheduling.changeDatesAria')}
            >
              {t('onsite.editor.workflow.displayWebContentMessage.scheduling.changeDates')}
            </LinkButton>
          </div>
        </SchedulingContent>
        <SchedulingDefaultVariant splitTestId={splitTestId} key={splitTestId} />
      </ControlCard>

      <ScheduledDatesModal
        open={isScheduledDatesModalOpen}
        onClose={closeScheduledDatesModal}
        splitTestId={splitTestId}
      />
    </>
  );
};
